import { useCallback, useEffect, useState } from "react";
import { api, type SessionListEntry } from "../lib/api.js";
import { czyListaWymagaOdpytywania, kodBleduListy } from "../lib/wierszListy.js";

/** Co ile odswiezamy liste, dopoki cokolwiek sie liczy. */
export const INTERWAL_ODSWIEZANIA_LISTY_MS = 3000;

export interface WynikListySesji {
  /** null: jeszcze nie wczytana (albo pierwsze wczytanie sie nie udalo - wtedy `bladListy`). */
  sesje: SessionListEntry[] | null;
  /** Kod bledu pierwszego (nie cichego) wczytania. Lista zostaje null. */
  bladListy: string | null;
  /** S-7 plus BN-1: ciche odswiezenie padlo, ale dane zostaja - to STAN, nie zdarzenie. */
  bladOdswiezania: boolean;
  /** `ciche: true` - odswiezenie w tle (nie kasuje danych, nie zamienia widoku w blad); false - zwykle wczytanie. */
  pobierz: (ciche: boolean) => Promise<void>;
}

/**
 * Lista analiz konta z odpytywaniem. Wspolna dla listy administratora (pages/SessionsPage.tsx) i listy "Moje rozmowy"
 * (pages/uzytkownik/StartPage.tsx).
 *
 * S-7: odswiezenie w tle NIE kasuje danych, nie pokazuje spinnera i nie zamienia widoku w stan bledu. Wczesniej
 * `catch { setSessions([]) }` polykalo blad pobrania i renderowalo awarie backendu jako "nie masz zadnych analiz" -
 * dokladnie wbrew P-6 ("blad nie jest pustka").
 *
 * Odswiezanie tylko wtedy, gdy cokolwiek sie liczy. Staly interwal odpytywalby serwer w nieskonczonosc na ekranie,
 * ktory sie nie zmienia. Sesja, ktora czeka na zatwierdzenie podzialu, nie zmieni sie sama, wiec tez nie wlacza
 * odpytywania.
 */
export function useListaSesji(): WynikListySesji {
  const [sesje, setSesje] = useState<SessionListEntry[] | null>(null);
  const [bladListy, setBladListy] = useState<string | null>(null);
  const [bladOdswiezania, setBladOdswiezania] = useState(false);

  const pobierz = useCallback(async (ciche: boolean) => {
    try {
      const data = await api.sessions();
      setSesje(data.sessions);
      setBladListy(null);
      setBladOdswiezania(false);
    } catch (err) {
      if (ciche) setBladOdswiezania(true);
      else setBladListy(kodBleduListy(err));
    }
  }, []);

  useEffect(() => {
    void pobierz(false);
  }, [pobierz]);

  useEffect(() => {
    if (!czyListaWymagaOdpytywania(sesje)) return;
    const t = setInterval(() => void pobierz(true), INTERWAL_ODSWIEZANIA_LISTY_MS);
    return () => clearInterval(t);
  }, [sesje, pobierz]);

  return { sesje, bladListy, bladOdswiezania, pobierz };
}
